import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { formatNumber } from "src/utils/numberFormat";
import type { LeadNote, SentimentBar, SourceCoverageRow } from "../Types/ListeningReportTypes";
import { CardTitle, REPORT_KEY, ReportBadge, ReportTag, SectionTitle, getBrandScope } from "./ReportPrimitives";

const SENTIMENT_KEY = `${REPORT_KEY}.sentiment`;

const SentimentLegend: FC = () => {
  const { t } = useTranslation();
  return (
    <div className="br__legend">
      <span className="br__chip">
        <i className="br__dot br__dot--pos" />
        {t(`${SENTIMENT_KEY}.positive`, "Positive")}
      </span>
      <span className="br__chip">
        <i className="br__dot br__dot--neu" />
        {t(`${SENTIMENT_KEY}.neutral`, "Neutral")}
      </span>
      <span className="br__chip">
        <i className="br__dot br__dot--neg" />
        {t(`${SENTIMENT_KEY}.negative`, "Negative")}
      </span>
    </div>
  );
};

const SentimentBarRow: FC<{ bar: SentimentBar }> = ({ bar }) => {
  const { t, i18n } = useTranslation();
  const formatPercent = (value: number) =>
    `${formatNumber(value, { minimumFractionDigits: 1, maximumFractionDigits: 1 }, i18n.language)}%`;
  const { positive, neutral, negative } = bar.share;

  return (
    <div className="br__bar-row">
      <div className="br__bar-top">
        <b>
          {bar.name}
          {bar.flag && (
            <span className="br__bar-flag">
              <ReportTag small tone="neg">
                {bar.flag}
              </ReportTag>
            </span>
          )}
        </b>
        <span>{t(`${SENTIMENT_KEY}.items`, "{{count}} items", { count: bar.items })}</span>
      </div>
      <div
        className="br__stack"
        role="img"
        aria-label={t(
          `${SENTIMENT_KEY}.barLabel`,
          "{{name}}: {{positive}} positive, {{neutral}} neutral, {{negative}} negative",
          {
            name: bar.name,
            positive: formatPercent(positive),
            neutral: formatPercent(neutral),
            negative: formatPercent(negative),
          }
        )}
      >
        <span className="br__stack-pos" style={{ width: `${positive}%` }} />
        <span className="br__stack-neu" style={{ width: `${neutral}%` }} />
        <span className="br__stack-neg" style={{ width: `${negative}%` }} />
      </div>
      <div className="br__bar-vals">
        <span>
          <i className="br__dot br__dot--pos" />
          {formatPercent(positive)}
        </span>
        <span>
          <i className="br__dot br__dot--neu" />
          {formatPercent(neutral)}
        </span>
        <span>
          <i className="br__dot br__dot--neg" />
          {formatPercent(negative)}
        </span>
      </div>
    </div>
  );
};

interface SentimentCardProps {
  title: string;
  help: string;
  description: string;
  bars: SentimentBar[];
  note: LeadNote;
}

const SentimentCard: FC<SentimentCardProps> = ({ title, help, description, bars, note }) => (
  <div className="br__card br__c6">
    <div className="br__card-top">
      <div>
        <CardTitle help={help}>{title}</CardTitle>
        <p className="br__card-desc">{description}</p>
      </div>
      <SentimentLegend />
    </div>
    {bars.map(bar => (
      <SentimentBarRow key={bar.name} bar={bar} />
    ))}
    <div className="br__note-box">
      <b>{note.lead}</b> {note.body}
    </div>
  </div>
);

const SourceCoverageTable: FC<{ rows: SourceCoverageRow[]; note: string }> = ({ rows, note }) => {
  const { t, i18n } = useTranslation();
  const format = (value: number) => formatNumber(value, {}, i18n.language);

  return (
    <div className="br__card br__card--flush">
      <div className="br__card-top">
        <div>
          <CardTitle
            help={t(`${SENTIMENT_KEY}.coverageHelp`, "Every source the agent checked, per brand")}
          >
            {t(`${SENTIMENT_KEY}.coverageTitle`, "Source Coverage")}
          </CardTitle>
          <p className="br__card-desc">{note}</p>
        </div>
      </div>
      <div className="br__table-wrap">
        <table className="br__table">
          <thead>
            <tr>
              <th>{t(`${SENTIMENT_KEY}.brand`, "Brand")}</th>
              <th>{t(`${SENTIMENT_KEY}.source`, "Source")}</th>
              <th>{t(`${SENTIMENT_KEY}.status`, "Status")}</th>
              <th className="br__num">{t(`${SENTIMENT_KEY}.posts`, "Posts")}</th>
              <th className="br__num">{t(`${SENTIMENT_KEY}.comments`, "Comments")}</th>
              <th>{t(`${SENTIMENT_KEY}.scoredItems`, "Scored items")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={`${row.brand}-${row.source}`} className={row.collected ? undefined : "br__row--off"}>
                <td>{row.brand}</td>
                <td>
                  <ReportTag small>{row.source}</ReportTag>
                </td>
                <td>
                  <ReportBadge tone={row.collected ? "pos" : "neu"}>
                    {row.collected
                      ? t(`${SENTIMENT_KEY}.collected`, "collected")
                      : t(`${SENTIMENT_KEY}.noData`, "no data")}
                  </ReportBadge>
                </td>
                <td className="br__num">{format(row.posts)}</td>
                <td className="br__num">{format(row.comments)}</td>
                <td>
                  {row.scoredItems === undefined ? (
                    "–"
                  ) : (
                    <div className="br__mini">
                      <div className="br__mini-track">
                        <div className="br__mini-fill" style={{ width: `${row.scoredPercent ?? 0}%` }} />
                      </div>
                      <span>{format(row.scoredItems)}</span>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

interface SentimentSectionProps {
  competitive?: { bars: SentimentBar[]; note: LeadNote };
  platform: { bars: SentimentBar[]; note: LeadNote };
  sourceCoverage: SourceCoverageRow[];
  sourceCoverageNote: string;
  primaryName: string;
  competitorName?: string;
}

const SentimentSection: FC<SentimentSectionProps> = ({
  competitive,
  platform,
  sourceCoverage,
  sourceCoverageNote,
  primaryName,
  competitorName,
}) => {
  const { t } = useTranslation();

  return (
    <section className="br__panel" id="br-sentiment" data-pdf-block="br-sentiment" data-pdf-splittable aria-label={t(`${REPORT_KEY}.tabs.sentiment`, "Sentiment")}>
      <SectionTitle scope={getBrandScope(t, primaryName, competitorName)}>
        {t(`${REPORT_KEY}.tabs.sentiment`, "Sentiment")}
      </SectionTitle>
      <div className="br__grid">
        {competitive && (
          <SentimentCard
          title={t(`${SENTIMENT_KEY}.competitiveTitle`, "Competitive Sentiment")}
          help={t(`${SENTIMENT_KEY}.competitiveHelp`, "Share of scored items by sentiment for each brand")}
          description={t(
            `${SENTIMENT_KEY}.competitiveDescription`,
            "{{primary}} compared with {{competitor}}, by share of scored items.",
            { primary: primaryName, competitor: competitorName }
          )}
          bars={competitive.bars}
          note={competitive.note}
        />
        )}
        <SentimentCard
          title={t(`${SENTIMENT_KEY}.platformTitle`, "{{primary}} Sentiment by Platform", { primary: primaryName })}
          help={t(`${SENTIMENT_KEY}.platformHelp`, "Share of {{primary}} items by sentiment on each platform", { primary: primaryName })}
          description={t(
            `${SENTIMENT_KEY}.platformDescription`,
            "Where {{primary}}’s positive and negative conversation comes from.",
            { primary: primaryName }
          )}
          bars={platform.bars}
          note={platform.note}
        />
      </div>
      <SourceCoverageTable rows={sourceCoverage} note={sourceCoverageNote} />
    </section>
  );
};

export default SentimentSection;
